import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { SessionStateService } from './session-state.service';
import { TranslateService } from '@ngx-translate/core';

export interface User {
  id: string;
  name: string;
  email: string;
  wordsToUse: number;
  wordsUsed: number;
  documentsToUse: number;
  documentsUsed: number;
  allowedLanguages: string[] | null;
  nrOfDrafts: number;
  nrOfDraftsUsed: number;
  status: number;
  createdAt: string;
  updatedAt: string;
  roleId: number;
  institutionId: string;
  indexId: string | null;
  facultyId: string;
  departmentId: string;
  provider: string | null;
  phoneNumber: string | null;
  lastLogin: string;
  thirdPartyEmailAct: boolean;
  marketingEmails: boolean;
  isDeleted: boolean;
  registrantId: string | null;
  language: string;
  Institution: {
    id: string;
    name: string;
    description: string;
    ltiSecret: string | null;
    createdAt: string;
    updatedAt: string;
    settings: any;
    clientId: string | null;
    InstitutionLicense: any[];
  };
}

export interface SharingContext {
  isShared: boolean;
  isViewOnly: boolean;
  userRole: 'owner' | 'recipient';
  shareType?: 'link_share' | 'email_share';
  accessType?: 'time_based' | 'unlimited' | 'one_time';
  accessPermission?: 'view' | 'edit';
  sharedBy?: {
    name: string;
    email: string;
  };
  shareMessage?: string | null;
  shareCreatedAt?: string;
  isPrecheckShared?: boolean;
}

export interface ShareDetails {
  share: {
    id: string;
    submissionId: string;
    shareType: 'email_share' | 'link_share';
    accessType: 'unlimited' | 'one_time' | 'time_based';
    accessPermission: 'view' | 'edit';
    shareCreatedAt: string;
    expiresAt?: string;
    expiredAccessMessage?: string;
    status: string;
    sharedBy: {
      id: string;
      name: string;
      email: string;
    };
    submission: {
      id: string;
      title: string;
      filename: string;
      createdAt: string;
    };
  };
  othersWithAccess: {
    total: number;
    shares: Array<{
      shareType: 'email_share' | 'link_share';
      accessType: 'unlimited' | 'one_time' | 'time_based';
      accessPermission: 'view' | 'edit';
      sharedBy: string;
      shareCreatedAt: string;
      recipientInfo: string;
    }>;
  };
}

export interface SessionResponse {
  valid: boolean;
  user: User;
  submissionId: string;
  token: string;
  iframeUrl?: string;
  sharingContext: SharingContext;
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private static readonly GENERIC_EXPIRED_SHARE_MESSAGE =
    'This shared link has expired and is no longer accessible. Time-limited shares automatically expire for security purposes. Please contact the document owner to request a new sharing link if you still need access.';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private sessionState: SessionStateService,
    private translate: TranslateService
  ) {
    this.restoreSessionFromUrl();
  }

  /**
   * Restore session from URL query params (after page refresh)
   * Uses localStorage token if available, avoiding unnecessary verifySession calls
   */
  private restoreSessionFromUrl(): void {
    const urlTree = this.router.parseUrl(this.router.url);
    const sessionIdFromUrl = urlTree.queryParams['sessionId'];

    if (sessionIdFromUrl && !this.sessionState.getSessionId()) {
      const existingToken = localStorage.getItem('ACCESS_TOKEN');

      if (existingToken) {
        this.sessionState.setSessionId(sessionIdFromUrl);
      }
    }
  }

  /**
   * Re-validates time-based shared access on page refresh before reloading report data.
   * One-time shares keep using submissionDetails + skipViewCount for their own message.
   */
  revalidateSharedSessionOnRefresh(sessionId: string): Observable<void> {
    return this.getShareDetails(sessionId).pipe(
      switchMap((details) => {
        if (details.share.accessType !== 'time_based') {
          return of(undefined); // continue loading report
        }

        if (this.isTimeBasedShareExpired(details.share)) {
          this.redirectToAccessInfo(this.getExpiredShareAccessMessage(details.share));
          return EMPTY;
        }

        return of(undefined); // continue loading report
      }),
      catchError((error) => {
        const message = error?.error?.message;
        if (
          message === 'api_tokens.session_has_expired' ||
          (typeof message === 'string' && message.startsWith('This shared link expired on'))
        ) {
          this.redirectToAccessInfo(message);
          return EMPTY;
        }
        return of(undefined);
      })
    );
  }

  private isTimeBasedShareExpired(share: ShareDetails['share']): boolean {
    if (share.accessType !== 'time_based') {
      return false;
    }

    if (share.status === 'expired') {
      return true;
    }

    return !!share.expiresAt && new Date(share.expiresAt) <= new Date();
  }

  getExpiredShareAccessMessage(share: ShareDetails['share']): string {
    if (share.expiredAccessMessage) {
      return share.expiredAccessMessage;
    }

    if (share.expiresAt) {
      const expiredDate = new Date(share.expiresAt).toLocaleString();
      return `This shared link expired on ${expiredDate} and is no longer accessible. Time-limited shares automatically expire for security purposes. Please contact the document owner to request a new sharing link if you still need access.`;
    }

    return SessionService.GENERIC_EXPIRED_SHARE_MESSAGE;
  }

  resolveAccessInfoReason(reason: string): string {
    if (
      reason.startsWith('This shared link expired on') ||
      reason.startsWith('This shared link has expired')
    ) {
      return reason;
    }

    if (reason === 'api_tokens.session_has_expired') {
      return SessionService.GENERIC_EXPIRED_SHARE_MESSAGE;
    }

    const translated = this.translate.instant(reason);
    return translated !== reason ? translated : reason;
  }

  redirectToAccessInfo(reason: string): void {
    const resolvedReason = this.resolveAccessInfoReason(reason);
    const baseUrl = environment.ioApplicationUrl || '/';
    window.location.replace(`${baseUrl}notfound?reason=${encodeURIComponent(resolvedReason)}`);
  }

  /**
   * Verifies a session with the backend
   * @param sessionId The session ID to verify
   * @param forceVerify If true, forces verification even if token exists (default: false)
   * @returns Observable with session data
   */
  verifySession(sessionId: string, forceVerify: boolean = false): Observable<SessionResponse> {
    const existingToken = localStorage.getItem('ACCESS_TOKEN');
    const storedSessionId = this.sessionState.getSessionId();

    if (!forceVerify && existingToken && storedSessionId === sessionId) {
      return new Observable(observer => {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          observer.next({
            valid: true,
            token: JSON.parse(existingToken),
            user: user,
            submissionId: '',
            sharingContext: this.getSharingContext() || {} as SharingContext
          } as SessionResponse);
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      });
    }

    return this.http.post<SessionResponse>(
      `${environment.apiUrl}/external-sessions/verify`,
      { sessionId }
    ).pipe(
      map((response: SessionResponse) => {
        if (response.valid && response.token) {

          localStorage.setItem('ACCESS_TOKEN', JSON.stringify(response.token));
          localStorage.setItem('role', JSON.stringify(response.user.roleId));
          localStorage.setItem('user', JSON.stringify(response.user));
          localStorage.setItem('websiteLanguage', response.user.language || 'en');
          this.storeSharingContext(response.sharingContext, sessionId);
        }
        return response;
      })
    );
  }

  /**
   * Stores sharing context in memory state
   * @param sharingContext The sharing context from session response
   * @param sessionId The original session ID
   */
  storeSharingContext(sharingContext: SharingContext, sessionId: string): void {
    this.sessionState.setSharingContext(sharingContext);
    this.sessionState.setSessionId(sessionId);
  }

  /**
   * Retrieves the sharing context from state
   * @returns The sharing context if available
   */
  getSharingContext(): SharingContext | null {
    return this.sessionState.getSharingContext();
  }

  /**
   * Checks if the current session is a shared session
   */
  isSharedSession(): boolean {
    return this.sessionState.isSharedSession();
  }

  /**
   * Checks if the current session is view-only
   */
  isViewOnlySession(): boolean {
    return this.sessionState.isViewOnlySession();
  }

  /**
   * Gets the current user's role in the sharing context
   */
  getUserRole(): 'owner' | 'recipient' | null {
    return this.sessionState.getUserRole();
  }

  /**
   * Clears sharing context from state
   */
  clearSharingContext(): void {
    this.sessionState.clearSessionState();
  }

  /**
   * Clears ALL session data - used for security breaches
   */
  private clearAllSessionData(): void {
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    localStorage.removeItem('websiteLanguage');
    this.clearSharingContext();

    // Clear any other potential session-related data
    localStorage.clear();
  }

  /**
   * Redirects to the appropriate report page based on session data
   * @param sessionData The session response data
   * @param queryParams Optional query parameters to preserve in the URL
   */
  navigateToReport(sessionData: SessionResponse, queryParams?: any): void {
    const { submissionId } = sessionData;

    if (!submissionId) {
      this.router.navigate(['/not-found']);
      return;
    }

    // Get sessionId to include in URL
    const sessionId = this.sessionState.getSessionId();

    // For IO route with sessionId in query params
    this.router.navigate([
      '/submission',
      submissionId,
      'report',
      'v2'
    ], { 
      queryParams: { 
        ...queryParams,
        sessionId: sessionId  // Add sessionId to URL
      } 
    });
  }

  /**
   * Clears all session data
   */
  clearSession(): void {
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    localStorage.removeItem('websiteLanguage');
    this.clearSharingContext();
  }

  /**
   * Enhanced security check for sensitive operations
   * @returns boolean indicating if the current session can perform the operation
   */
  canPerformSensitiveOperation(): boolean {
    // Get the context
    const context = this.getSharingContext();
    if (!context) {
      return false;
    }

    // Additional security checks
    if (context.isShared && context.isViewOnly) {
      console.warn('Operation denied: view-only access');
      return false;
    }

    if (context.userRole === 'recipient' && context.isShared) {
      console.warn('Operation denied: recipient role in shared session');
      return false;
    }

    return true;
  }

  /**
   * Re-validates session with backend (for critical operations)
   * @returns Observable with fresh session data
   */
  revalidateSession(): Observable<boolean> {
    const storedSessionId = this.sessionState.getSessionId();
    if (!storedSessionId) {
      return new Observable(observer => {
        observer.next(false);
        observer.complete();
      });
    }

    return this.verifySession(storedSessionId).pipe(
      map(response => response.valid)
    );
  }

  /**
   * Get session ID from state
   * @returns The current session ID
   */
  getSessionId(): string | null {
    return this.sessionState.getSessionId();
  }

  /**
   * Get details about a specific share
   * @param sessionId The session ID of the share to fetch
   * @returns Observable with share details including others with access
   */
  getShareDetails(sessionId: string): Observable<ShareDetails> {
    return this.http.get<ShareDetails>(`${environment.apiUrl}/get/shares/${sessionId}`);
  }
}
